import React, { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Marker, Polygon } from "react-leaflet";
import { BottomSheet } from 'react-spring-bottom-sheet';
import 'react-spring-bottom-sheet/dist/style.css';
import L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { useLocation as useRouterLocation } from 'react-router-dom';

// Import components
import SearchBar from '../Search/SearchBar';
import FilterBar from '../Filters/FilterBar';
import DurationSelector from '../Filters/DurationSelector';
import ToggleSwitch from '../Filters/ToggleSwitch';
import POIList from '../POI/POIList';
import POIDetails from '../POI/POIDetails';
import EVStationInfo from '../EVStation/EVStationInfo';
import MapControls from './MapControls';
import LoadingOverlay from '../UI/LoadingOverlay';
import ErrorMessage from '../UI/ErrorMessage';
import WelcomeMessage from '../UI/WelcomeMessage';
import POIDetailsMobile from '../POI/POIDetailsMobile';

// Import utilities
import {
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  BOTTOM_SHEET_SNAP_HEIGHT,
  TILE_URL,
  TILE_ATTRIBUTION,
  poiFilters,
  DURATION_OPTIONS,
  PECLET_API_KEY,
  HUNTER_API_KEY
} from '../../utils/constants';
import { createEvIcon, getPoiIcon, createClusterCustomIcon, panToStation } from '../../utils/mapUtils';
import { calculateDistance, formatDistance, deg2rad } from '../../utils/formatters';

// Import styles
import '../../styles/MapView.css';
import sleeveButton from '../../assets/icons/sleeve-button.svg';
import feedbackIcon from '../../assets/icons/feedback.svg';
import shareFeedbackIcon from '../../assets/icons/share-feedback.svg';
import reportIcon from '../../assets/icons/report.svg';
import logo from '../../assets/icons/favicon.png';
import eventureMapLogo from '../../assets/logos/eventure-map-logo.png';
import pecletLogo from '../../assets/logos/powered-by-peclet.svg'

// Responsive hook
function useMediaQuery(query) {
  const [matches, setMatches] = useState(window.matchMedia(query).matches);
  useEffect(() => {
    const media = window.matchMedia(query);
    if (media.matches !== matches) setMatches(media.matches);
    const listener = () => setMatches(media.matches);
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [matches, query]);
  return matches;
}

export default function MapView() {
  const location = useRouterLocation();
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  // State management
  const [selectedDuration, setSelectedDuration] = useState(null);
  const [activePoiFilters, setActivePoiFilters] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [disabledAccessOnly, setDisabledAccessOnly] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showAllEVs, setShowAllEVs] = useState(true);
  const [selectedPoi, setSelectedPoi] = useState(null);
  const [showWelcomeSheet, setShowWelcomeSheet] = useState(true);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  // Location state
  const [userLocation, setUserLocation] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  const [locationError, setLocationError] = useState(null);

  // Debug location error changes
  useEffect(() => {
    // console.log('locationError changed:', locationError);
  }, [locationError]);

  // Map data state
  const [evStations, setEvStations] = useState([]);
  const [filteredStations, setFilteredStations] = useState([]);
  const [selectedStation, setSelectedStation] = useState(null);
  const [isolines, setIsolines] = useState([]);
  const [poiList, setPoiList] = useState([]);
  const [filteredPoiList, setFilteredPoiList] = useState([]);
  const [isLoadingPois, setIsLoadingPois] = useState(false);

  // Refs
  const mapRef = useRef();
  const bottomSheetRef = useRef();
  const welcomeShownRef = useRef(false);

  // EV Icon
  const evIcon = createEvIcon(32);
  const evIconSelected = createEvIcon(48);

  // Fetch EV stations on component mount
  useEffect(() => {
    const fetchEvStations = async () => {
      try {
        const response = await fetch(`https://data.peclet.com.au/api/explore/v2.1/catalog/datasets/ev-charging-stations/records?select=station_name%2Cstation_address%2Coperator%2Clatitude%2Clongitude%2Ccharger_capacities%2Cev_station_id&where=status%3D%27Existing%27&group_by=station_name%2Cstation_address%2Coperator%2Clatitude%2Clongitude%2Ccharger_capacities%2Cev_station_id&order_by=station_name%2Cstation_address%2Coperator%2Clatitude%2Clongitude%2Ccharger_capacities%2Cev_station_id&apikey=${PECLET_API_KEY}`);
        const data = await response.json();
        // console.log('EV stations loaded:', data.results.length, 'stations');
        setEvStations(data.results);
        setFilteredStations(data.results);
      } catch (error) {
        console.error('Error fetching EV stations:', error);
      }
    };
    fetchEvStations();
  }, []);

  // Handle location-based navigation
  useEffect(() => {
    if (location.state?.shouldLocate) {
      // Clear previous selections and errors
      setSelectedStation(null);
      setSelectedDuration(10);
      setActivePoiFilters([]);
      setFilteredStations(evStations);
      setSearchSuggestions([]);
      setShowSuggestions(false);
      setIsolines([]);
      setPoiList([]);
      setSheetOpen(false);
      setShowWelcomeSheet(false);
      setLocationError(null);

      // Get user location
      if (navigator.geolocation) {
        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setUserLocation({ lat: latitude, lng: longitude });
            setIsLocating(false);
          },
          (error) => {
            // console.error('Error getting location:', error);
            setIsLocating(false);

            // Provide user-friendly error messages
            switch (error.code) {
              case error.PERMISSION_DENIED:
                // Check if it's an HTTPS issue
                if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
                  setLocationError("Geolocation requires a secure connection (HTTPS). Please access this app via HTTPS or use localhost instead of an IP address.");
                } else {
                  setLocationError("Please enable location access in your browser settings to find nearby EV chargers.");
                }
                break;
              case error.POSITION_UNAVAILABLE:
                setLocationError("Location information is unavailable. Please check your device settings.");
                break;
              case error.TIMEOUT:
                setLocationError("Location request timed out. Please try again.");
                break;
              default:
                setLocationError("Unable to get your location. Please check your browser settings.");
            }
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        );
      } else {
        setLocationError("Geolocation is not supported by your browser.");
      }
    }
  }, [location.key, location.state?.shouldLocate]);

  // Handler for auto-location button (find nearest EV charger)
  const handleLocationRequest = () => {
    // console.log('handleLocationRequest called');
    setLocationError(null);

    if (!navigator.geolocation) {
      setLocationError("Geolocation is not supported by your browser.");
      return;
    }

    setIsLocating(true);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // console.log('Location success:', position.coords);
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        setIsLocating(false);
        setLocationError(null);

        // Find nearest station if stations are available
        if (evStations && evStations.length > 0) {
          // console.log('Finding nearest station from', evStations.length, 'stations');
          const nearestStation = evStations.reduce((nearest, current) => {
            const nearestDist = calculateDistance(
              latitude, longitude,
              nearest.latitude, nearest.longitude
            );
            const currentDist = calculateDistance(
              latitude, longitude,
              current.latitude, current.longitude
            );
            return currentDist < nearestDist ? current : nearest;
          }, evStations[0]);

          // console.log('Nearest station found:', nearestStation.station_name, 'at distance:', calculateDistance(latitude, longitude, nearestStation.latitude, nearestStation.longitude), 'km');
          setSearchTerm(nearestStation.station_name);
          selectStation(nearestStation);

          // Pan to the nearest station
          if (mapRef.current) {
            panToStation(mapRef.current, nearestStation, BOTTOM_SHEET_SNAP_HEIGHT);
          }
        } else {
          // console.log('No EV stations available to find nearest');
        }
      },
      (error) => {
        // console.log('Location error:', error.code, error.message);
        setIsLocating(false);

        switch (error.code) {
          case error.PERMISSION_DENIED:
            // Check if it's an HTTPS issue
            if (window.location.protocol === 'http:' && window.location.hostname !== 'localhost') {
              setLocationError("Geolocation requires a secure connection (HTTPS). Please access this app via HTTPS or use localhost instead of an IP address.");
            } else {
              setLocationError("Please enable location access in your browser settings to find nearby EV chargers.");
            }
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationError("Location information is unavailable. Please check your device settings.");
            break;
          case error.TIMEOUT:
            setLocationError("Location request timed out. Please try again.");
            break;
          default:
            setLocationError("Unable to get your location. Please check your browser settings.");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // Select a station
  const selectStation = (station) => {
    // console.log('selectStation called with:', station.station_name);

    if (selectedStation?.ev_station_id === station.ev_station_id) {
      setShowSuggestions(false);
      return;
    }

    setSearchTerm(station.station_name);
    setSelectedStation(station);
    setFilteredStations(evStations);
    setShowAllEVs(true);
    setShowSuggestions(false);
    setSelectedDuration(10);
    setSheetOpen(true);
    setShowWelcomeSheet(false);
    // setIsolines([]);
    // setPoiList([]);
    // reset POIs details when switching EV pins
    setSelectedPoi(null)


    if (mapRef.current) {
      panToStation(mapRef.current, station, BOTTOM_SHEET_SNAP_HEIGHT);
    }
  };

  // Clear all data
  const clearData = () => {
    setSelectedStation(null);
    setFilteredStations(evStations);
    setIsolines([]);
    setPoiList([]);
  };

  // Fetch POIs
  const fetchPOIs = async (polygons, selectedStation) => {
    // console.log('fetchPOIs called with:', polygons.length, 'polygons for station:', selectedStation.station_name);
    if (!polygons || polygons.length === 0 || !selectedStation) {
      setPoiList([]);
      return;
    }

    try {
      // First, get POIs within isoline
      const closedPolygons = polygons.map(p => {
        const closed = [...p];
        if (closed.length > 0 && (closed[0][0] !== closed[closed.length - 1][0] || closed[0][1] !== closed[closed.length - 1][1])) {
          closed.push(closed[0]);
        }
        return closed;
      });

      const multiPolygonStr = closedPolygons
        .map(p => `((${p.map(([lat, lng]) => `${lng} ${lat}`).join(',')}))`)
        .join(',');

      const group_by_params = 'poi_name,poi_category,poi_description,additional_information,poi_street,poi_suburb,poi_postcode,latitude,longitude,image_url,website,phone,opening_hours_days,disable_access_y_n,entry_fee,google_maps_direction';
      const isolineQuery = `geometry(\`geopoint\`, geom'MULTIPOLYGON(${multiPolygonStr})')`;
      const isolineUrl = `https://newcastlenswiar.opendatasoft.com/api/explore/v2.1/catalog/datasets/ev-tourism-map-points-of-interest/records?where=(${encodeURIComponent(isolineQuery)})&group_by=${encodeURIComponent(group_by_params)}&apikey=${HUNTER_API_KEY}`;

      // Second, get POIs within 2km radius
      const radiusQuery = `distance(\`geopoint\`, geom'POINT(${selectedStation.longitude} ${selectedStation.latitude})', 2000m)`;
      const radiusUrl = `https://newcastlenswiar.opendatasoft.com/api/explore/v2.1/catalog/datasets/ev-tourism-map-points-of-interest/records?where=(${encodeURIComponent(radiusQuery)})&group_by=${encodeURIComponent(group_by_params)}&apikey=${HUNTER_API_KEY}`;

      // console.log('Fetching POIs from isoline and radius...');

      // Fetch both sets of POIs
      const [isolineRes, radiusRes] = await Promise.all([
        fetch(isolineUrl),
        fetch(radiusUrl)
      ]);

      const [isolineData, radiusData] = await Promise.all([
        isolineRes.json(),
        radiusRes.json()
      ]);

      // console.log('POI API responses:', { isoline: isolineData.results?.length || 0, radius: radiusData.results?.length || 0 });

      // Combine and deduplicate POIs
      const allPois = [...(isolineData.results || []), ...(radiusData.results || [])];
      const uniquePois = Array.from(new Map(allPois.map(poi => [poi.poi_name, poi])).values());

      // Filter and add distance information
      const filteredPois = uniquePois
        .filter(poi => poi.latitude && poi.longitude && poi.poi_name && poi.poi_category)
        .map(poi => ({
          ...poi,
          distance: calculateDistance(
            selectedStation.latitude,
            selectedStation.longitude,
            poi.latitude,
            poi.longitude
          )
        }))
        .sort((a, b) => a.distance - b.distance);

      // console.log('Final POIs to display:', filteredPois.length);
      setPoiList(filteredPois);
    } catch (error) {
      console.error('Error fetching POIs:', error);
      setPoiList([]);
    }
  };

  // Fetch isolines
  const fetchIsolines = async (station, duration, mapRef) => {
    // console.log('fetchIsolines called with:', station.station_name, 'duration:', duration);
    if (!station || !duration) return;

    setIsLoadingPois(true);
    // setIsolines([]);
    // setPoiList([]);

    try {
      const evStationId = station.ev_station_id;
      const isolineApiUrl = `https://newcastlenswiar.opendatasoft.com/api/explore/v2.1/catalog/datasets/ev-charging-stations-isolines/records?refine=ev_station_id%3A${encodeURIComponent(evStationId)}&refine=isoline_type%3A${duration}mn%20Walk&apikey=${HUNTER_API_KEY}`;
      // console.log('Fetching isoline from:', isolineApiUrl);
      const response = await fetch(isolineApiUrl);
      if (!response.ok) throw new Error("Failed to fetch isolines");
      const data = await response.json();
      // console.log('Isoline API response:', data);

      const polygons = data.results
        .filter(record => record.geo_shape?.geometry?.coordinates)
        .flatMap(record => {
          const { type, coordinates } = record.geo_shape.geometry;
          if (type === 'Polygon') return [coordinates[0].map(coord => [coord[1], coord[0]])];
          if (type === 'MultiPolygon') return coordinates.map(poly => poly[0].map(coord => [coord[1], coord[0]]));
          return [];
        });

      // console.log('Processed polygons:', polygons.length);

      if (polygons.length > 0) {
        setIsolines(polygons);
        // console.log('Setting isolines, now fetching POIs...');
        await fetchPOIs(polygons, station);

        // Fit map to isoline bounds if map is available
        // if (mapRef && mapRef.current) {
        //   const bounds = L.latLngBounds(polygons.flat());
        //   mapRef.current.fitBounds(bounds, {
        //     paddingTopLeft: [50, 50],
        //     paddingBottomRight: [50, 350] // Account for bottom sheet
        //   });
        // }
      } else {
        // console.log('No isoline polygons found, panning to station');
        // Fallback: pan to station if no isoline found
        // if (mapRef && mapRef.current) {
        //   panToStation(mapRef.current, station, 350);
        // }
      }
    } catch (error) {
      console.error('Error fetching isolines:', error);
      // Fallback: pan to station on error
      // if (mapRef && mapRef.current) {
      //   panToStation(mapRef.current, station, 350);
      // }
    } finally {
      setIsLoadingPois(false);
    }
  };

  // Filter POIs based on active filters and disabled access
  useEffect(() => {
    const newFilteredList = poiList.filter(poi => {
      const disabledAccessMatch = !disabledAccessOnly || poi.disable_access_y_n === "Yes";

      if (activePoiFilters.length === 0) {
        return disabledAccessMatch;
      }

      const categoryMatch = poiFilters.some(
        f =>
          activePoiFilters.includes(f.name) &&
          (
            (Array.isArray(f.categoryValue) && f.categoryValue.includes(poi.poi_category)) ||
            poi.poi_category === f.categoryValue
          )
      );

      return disabledAccessMatch && categoryMatch;
    });
    setFilteredPoiList(newFilteredList);
  }, [poiList, activePoiFilters, disabledAccessOnly]);

  // Fetch isolines when station or duration changes
  useEffect(() => {
    // console.log('Isoline useEffect triggered:', { selectedStation: selectedStation?.station_name, selectedDuration });
    if (selectedStation && selectedDuration) {
      // console.log('Fetching isolines for:', selectedStation.station_name, 'duration:', selectedDuration);
      const timeoutId = setTimeout(() => {
        fetchIsolines(selectedStation, selectedDuration, mapRef);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [selectedStation, selectedDuration]);

  // Welcome sheet management
  useEffect(() => {
    if (!welcomeShownRef.current) {
      setShowWelcomeSheet(true);
      welcomeShownRef.current = true;
    } else if (selectedStation || searchTerm) {
      setShowWelcomeSheet(false);
    }
  }, [selectedStation, searchTerm]);

  // Add the dynamic height adjustment function
  const adjustDescriptionHeight = () => {
    const titleElement = document.querySelector('.poi-popup-panel .place-title');
    const contactElement = document.querySelector('.poi-popup-panel .place-contact-value');
    const descElement = document.querySelector('.poi-popup-panel .place-desc');

    if (titleElement && descElement) {
      const titleHeight = titleElement.offsetHeight;
      const contactHeight = contactElement ? contactElement.offsetHeight : 17; // Default to 17px if no contact

      let maxHeight = 100; // Default height

      // Case 1: Title is multi-line (64px) - reduce to 205px
      if (titleHeight > 40) {
        maxHeight = 180;
      }
      // Case 2: Title is single line (32px) AND contact is multi-line (34px) - reduce to 235px
      else if (titleHeight <= 40 && contactHeight > 25) {
        maxHeight = 220;
      }
      // Case 3: Title is single line (32px) AND contact is single line (17px) - keep 253px

      descElement.style.maxHeight = `${maxHeight}px`;
    }
  };

  // Add useEffect to adjust height when POI details change
  useEffect(() => {
    if (selectedPoi && isDesktop) {
      // Small delay to ensure DOM is fully rendered
      setTimeout(adjustDescriptionHeight, 100);
    }
  }, [selectedPoi, isDesktop]);

  // Event handlers
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value.length > 0) {
      const suggestions = evStations.filter(station =>
        station.station_name.toLowerCase().includes(value.toLowerCase()) ||
        station.station_address.toLowerCase().includes(value.toLowerCase())
      );
      setSearchSuggestions(suggestions);
      setShowSuggestions(true);
    } else {
      handleClearSearch();
    }
  };

  const handleStationSelect = (station) => {
    if (selectedStation?.ev_station_id === station.ev_station_id) {
      setShowSuggestions(false);
      return;
    }
    setSearchTerm(station.station_name);
    selectStation(station);
    setShowSuggestions(false);
    // setSelectedDuration(null);
    setSheetOpen(true);
    setShowWelcomeSheet(false);
    panToStation(mapRef.current, station, BOTTOM_SHEET_SNAP_HEIGHT);
  };

  const handleMarkerClick = (station) => {
    if (selectedStation?.ev_station_id === station.ev_station_id) {
      setSheetOpen(true);
      panToStation(mapRef.current, station, BOTTOM_SHEET_SNAP_HEIGHT);
    } else {
      handleStationSelect(station);
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    clearData();
    setActivePoiFilters([]);
    setSearchSuggestions([]);
    setShowSuggestions(false);
    setSelectedPoi(null);
    setSheetOpen(false);
    setShowWelcomeSheet(true);

    if (mapRef.current) {
      mapRef.current.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
    }
  };

  const handleFilterClick = (filterName) => {
    setActivePoiFilters(prev =>
      prev.includes(filterName)
        ? prev.filter(f => f !== filterName)
        : [...prev, filterName]
    );
  };

  const handlePoiSelect = (poi) => {
    setSelectedPoi(poi);
    setSheetOpen(true);
    setTimeout(() => {
      bottomSheetRef.current?.snapTo(({ snapPoints }) => Math.max(...snapPoints));
    }, 100);
  };

  const handleZoomIn = () => {
    if (mapRef.current) {
      mapRef.current.setZoom(mapRef.current.getZoom() + 1);
    }
  };

  const handleZoomOut = () => {
    if (mapRef.current) {
      mapRef.current.setZoom(mapRef.current.getZoom() - 1);
    }
  };

  // Find nearest station when both location and stations are available (for navigation)
  useEffect(() => {
    if (userLocation && evStations && evStations.length > 0 && location.state?.shouldLocate) {
      // console.log('Both location and stations available, finding nearest station');
      // Find nearest station with initial value
      const nearestStation = evStations.reduce((nearest, current) => {
        const nearestDist = calculateDistance(
          userLocation.lat, userLocation.lng,
          nearest.latitude, nearest.longitude
        );
        const currentDist = calculateDistance(
          userLocation.lat, userLocation.lng,
          current.latitude, current.longitude
        );
        return currentDist < nearestDist ? current : nearest;
      }, evStations[0]);

      // console.log('Nearest station found via navigation:', nearestStation.station_name, 'at distance:', calculateDistance(userLocation.lat, userLocation.lng, nearestStation.latitude, nearestStation.longitude), 'km');

      // Set search term and select the nearest station
      setSearchTerm(nearestStation.station_name);
      selectStation(nearestStation);

      // Pan to the nearest station
      if (mapRef.current) {
        panToStation(mapRef.current, nearestStation, BOTTOM_SHEET_SNAP_HEIGHT);
      }
    }
  }, [userLocation, evStations, location.state?.shouldLocate]);

  // Render methods
  const renderMapMarkers = () => (
    <>
      {userLocation && (
        <Marker
          position={[userLocation.lat, userLocation.lng]}
          icon={L.divIcon({
            className: 'user-location-marker',
            html: '<div class="user-location-dot"></div>',
            iconSize: [12, 12]
          })}
        />
      )}

      {showAllEVs && (
        <MarkerClusterGroup iconCreateFunction={createClusterCustomIcon}>
          {filteredStations.map((station, index) => (
            <Marker
              key={`${station.ev_station_id}-${index}`}
              position={[station.latitude, station.longitude]}
              icon={selectedStation?.ev_station_id === station.ev_station_id ? evIconSelected : evIcon}
              zIndexOffset={selectedStation?.ev_station_id === station.ev_station_id ? 1000 : 0}

              eventHandlers={{
                click: () => handleMarkerClick(station),
              }}
            />
          ))}
        </MarkerClusterGroup>
      )}

      {isolines.length > 0 && filteredPoiList.map((poi, idx) => (
        <Marker
          key={`poi-${idx}`}
          position={[poi.latitude, poi.longitude]}
          icon={getPoiIcon(poi.poi_category)}
          eventHandlers={{
            click: () => handlePoiSelect(poi)
          }}
        />
      ))}

      {isolines.length > 0 && isolines.map((polygonCoords, index) => (
        <Polygon
          key={index}
          positions={polygonCoords}
          pathOptions={{ color: '#2A5DA8', fillColor: '#004E98', fillOpacity: 0.2 }}
        />
      ))}
    </>
  );

  const renderFeedbackPopup = () => (
    isFeedbackOpen && (
      <div className="feedback-popup-overlay" onClick={() => setIsFeedbackOpen(false)}>
        <div className="feedback-popup" onClick={e => e.stopPropagation()}>
          <img src={logo} alt="Logo" className="feedback-logo" />
          <div className="feedback-links">

            <a href="https://tally.so/r/wzB0ER?form=feedback" target="_blank" rel="noopener noreferrer" className="feedback-link">
              <img src={shareFeedbackIcon} alt="Share feedback" className="feedback-link-icon" />
              Share feedback
            </a>
            <a href="https://tally.so/r/wzB0ER?form=report" target="_blank" rel="noopener noreferrer" className="feedback-link">
              <img src={reportIcon} alt="Report an improvement" className="feedback-link-icon" />
              Report an improvement
            </a>
          </div>
          <button className="feedback-close-btn" onClick={() => setIsFeedbackOpen(false)}>×</button>
        </div>
      </div>
    )
  );

  // Remove feedback button from renderSearchRow
  const renderSearchRow = () => (
    <div className="search-row">
      <SearchBar
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        onClearSearch={handleClearSearch}
        searchSuggestions={searchSuggestions}
        showSuggestions={showSuggestions}
        onSuggestionSelect={handleStationSelect}
      />
    </div>
  );

  // Add a new renderFeedbackButton function
  const renderFeedbackButton = () => (
    isDesktop ? (
      <button className="feedback-btn feedback-btn-desktop" onClick={() => setIsFeedbackOpen(true)}>
        <img src={feedbackIcon} alt="Feedback" />
        <span>Feedback</span>
      </button>
    ) : (
      <button className="feedback-btn feedback-btn-mobile" onClick={() => setIsFeedbackOpen(true)}>
        <img src={feedbackIcon} alt="Feedback" />
      </button>
    )
  );

  const renderFilterRow = () => (
    <FilterBar
      activeFilters={activePoiFilters}
      onFilterClick={handleFilterClick}
    />
  );

  const renderBottomSheetContent = () => {
    if (selectedPoi) {
      return isDesktop ? (
        <POIDetails
          poi={selectedPoi}
          onClose={() => setSelectedPoi(null)}
          isDesktop
        />
      ) : (
        <POIDetailsMobile
          poi={selectedPoi}
          onClose={() => setSelectedPoi(null)}
        />
      );
    }

    if (showWelcomeSheet) {
      return <WelcomeMessage />;
    }

    if (selectedStation) {
      return (
        <>
          <EVStationInfo station={selectedStation} />
          <DurationSelector
            selectedDuration={selectedDuration}
            onDurationSelect={setSelectedDuration}
            durations={DURATION_OPTIONS}
          />
          <ToggleSwitch
            checked={disabledAccessOnly}
            onChange={(e) => setDisabledAccessOnly(e.target.checked)}
          />
          <div className="places-title">Places close to you</div>
          <POIList
            poiList={poiList}
            filteredPoiList={filteredPoiList}
            selectedPoi={selectedPoi}
            isLoadingPois={isLoadingPois}
            selectedDuration={selectedDuration}
            onPoiSelect={handlePoiSelect}
          />
        </>
      );
    }

    return null;
  };

  // Desktop layout
  if (isDesktop) {
    return (
      <div className="mapview-desktop-root">
        {renderFeedbackPopup()}

        {locationError && (
          <ErrorMessage
            message={locationError}
            onClose={() => setLocationError(null)}
          />
        )}
        {/* Hamburger (sleeve) button - now static, no click */}
        <button
          className="sleeve-toggle-btn"
          style={{ zIndex: 1200 }}
        >
          <img src={sleeveButton} alt="Menu" className="sleeve-icon" />
        </button>
        {/* Sleeve panel - always visible, no open/close logic */}
        <aside className="sleeve-panel">
          <div className="sleeve-header">
            <div className="search-row">
              <SearchBar
                searchTerm={searchTerm}
                onSearchChange={handleSearchChange}
                onClearSearch={handleClearSearch}
                searchSuggestions={searchSuggestions}
                showSuggestions={showSuggestions}
                onSuggestionSelect={handleStationSelect}
              />
            </div>
          </div>
          <div className="sleeve-content">
            {showWelcomeSheet ? (
              <WelcomeMessage />
            ) : selectedStation && (
              <>
                <EVStationInfo station={selectedStation} />
                <DurationSelector
                  selectedDuration={selectedDuration}
                  onDurationSelect={setSelectedDuration}
                  durations={DURATION_OPTIONS}
                />
                <ToggleSwitch
                  checked={disabledAccessOnly}
                  onChange={(e) => setDisabledAccessOnly(e.target.checked)}
                />
                <div className="places-title">Places close to you</div>
                <POIList
                  poiList={poiList}
                  filteredPoiList={filteredPoiList}
                  selectedPoi={selectedPoi}
                  isLoadingPois={isLoadingPois}
                  selectedDuration={selectedDuration}
                  onPoiSelect={handlePoiSelect}
                />
              </>
            )}
          </div>
        </aside>

        {/* POI popup panel */}
        {selectedPoi && (
          <div className="poi-popup-panel">
            <POIDetails
              poi={selectedPoi}
              onClose={() => setSelectedPoi(null)}
              isDesktop
            />
          </div>
        )}

        {/* Map area */}
        <div className="map-area">
          <div className="category-row">
            <FilterBar
              activeFilters={activePoiFilters}
              onFilterClick={handleFilterClick}
            />
          </div>
          <div className="map-container">
            <MapContainer
              center={userLocation || DEFAULT_CENTER}
              zoom={DEFAULT_ZOOM}
              className="fullscreen-map"
              zoomControl={false}
              attributionControl={false}
              ref={mapRef}
            >
              <TileLayer
                url={TILE_URL}
                attribution={TILE_ATTRIBUTION}
              />
              {renderMapMarkers()}
            </MapContainer>
          </div>
          <MapControls
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onAutoLocate={handleLocationRequest}
            isLocating={isLocating}
          />

          {/* Eventure Map Logo - Desktop */}
          <div className="eventure-map-logo-desktop">
            <img className="ev-logo" src={eventureMapLogo} alt="Eventure Map Logo" />
            <img className="peclet-logo" src={pecletLogo} alt="Powered by Peclet" />
          </div>
        </div>
        {renderFeedbackButton()}
      </div>
    );
  }

  // Mobile layout
  return (
    <div className="map-root">
      {renderFeedbackPopup()}
      {isLocating && <LoadingOverlay />}

      {locationError && (
        <ErrorMessage
          message={locationError}
          onClose={() => setLocationError(null)}
        />
      )}

      <MapContainer
        center={userLocation || DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        className="fullscreen-map"
        zoomControl={false}
        attributionControl={false}
        ref={mapRef}
        eventHandlers={{
          dblclick: (e) => {
            if (mapRef.current) {
              mapRef.current.zoomIn();
            }
          },
        }}
      >
        <TileLayer
          url={TILE_URL}
          attribution={TILE_ATTRIBUTION}
        />
        {renderMapMarkers()}
      </MapContainer>



      <div className="map-ui">
        {renderSearchRow()}
        {renderFilterRow()}
      </div>

      <MapControls
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onAutoLocate={handleLocationRequest}
        isLocating={isLocating}
      />

      <BottomSheet
        scrollLocking={false}
        open={sheetOpen || showWelcomeSheet}
        onDismiss={() => {
          if (selectedPoi) {
            setSelectedPoi(null);
            return;
          }
          if (selectedStation) {
            bottomSheetRef.current?.snapTo(({ snapPoints }) => Math.min(...snapPoints));
            return;
          }
          setSheetOpen(false);
          setShowWelcomeSheet(false);
        }}
        ref={bottomSheetRef}
        skipInitialTransition
        defaultSnap={({ maxHeight }) => {
          if (showWelcomeSheet) return maxHeight / 2.5;
          if (selectedPoi) {
            if (selectedPoi.poi_description && selectedPoi.poi_description.trim().length > 120) {
              return maxHeight - 60;
            } else {
              return maxHeight / 1.5;
            }
          }
          if (selectedStation) return maxHeight / 2;
          return maxHeight / 2;
        }}
        snapPoints={({ maxHeight }) => {
          const MIN_SNAP_HEIGHT = 90;
          if (selectedPoi) {
            if (selectedPoi.poi_description && selectedPoi.poi_description.trim().length > 120) {
              return [
                maxHeight - 60,
                maxHeight / 1.5,
                MIN_SNAP_HEIGHT
              ];
            } else {
              return [
                maxHeight / 1.5,
                MIN_SNAP_HEIGHT
              ];
            }
          }
          if (showWelcomeSheet) {
            return [
              maxHeight / 3.5,
              MIN_SNAP_HEIGHT
            ];
          }
          if (selectedStation) {
            return [
              maxHeight - 60,
              maxHeight / 2,
              MIN_SNAP_HEIGHT
            ];
          }
          return [
            maxHeight / 2,
            MIN_SNAP_HEIGHT
          ];
        }}
        header={null}
        blocking={false}
        expandOnContentDrag={true}
      >
        <div className="bottom-sheet-content">
          {renderBottomSheetContent()}
          {/* Eventure Map Logo - Mobile */}
          <div className="eventure-map-logo-mobile">
            <img src={eventureMapLogo} alt="Eventure Map Logo" />
            <img className="peclet-logo" src={pecletLogo} alt="Powered by Peclet" />
          </div>
        </div>
      </BottomSheet>
      {renderFeedbackButton()}
    </div>
  );
} 
